import type { ReactNode } from 'react';

import { cx } from './cx';

export const INPUT_STYLES =
  'block w-full rounded-button border bg-surface px-4 text-ui-lg text-text-primary placeholder:text-text-secondary transition-colors hover:border-text-secondary';

/** ids for a field's hint and error, in the order aria-describedby reads them. */
export function describedBy(
  id: string,
  { error, hint }: { error?: string; hint?: ReactNode },
): string | undefined {
  const ids = [error ? `${id}-error` : null, hint ? `${id}-hint` : null].filter(
    Boolean,
  );
  return ids.length ? ids.join(' ') : undefined;
}

export function inputClass(error?: string, extra?: string) {
  return cx(
    INPUT_STYLES,
    // `text-muted` as a border is 3.3:1 on white — WCAG 1.4.11 needs 3:1
    // for a field's edge; the hairline the rest of the UI uses is ~1.2:1.
    error ? 'border-brand-strong' : 'border-text-muted',
    extra,
  );
}

/**
 * Label, hint and error around one control. The control itself is the
 * caller's, so it can be an input, select or textarea; the caller wires
 * `id`, `aria-invalid` and `aria-describedby` (see describedBy).
 */
export function Field({
  children,
  error,
  hint,
  id,
  label,
  optional,
}: {
  children: ReactNode;
  error?: string;
  hint?: ReactNode;
  id: string;
  label: string;
  optional?: boolean;
}) {
  return (
    <div>
      <label
        className="block text-ui-strong font-semibold text-text-primary"
        htmlFor={id}
      >
        {label}
        {optional ? (
          <span className="ml-1.5 font-normal text-text-secondary">
            (optional)
          </span>
        ) : null}
      </label>
      {hint ? (
        <p className="mt-1 text-ui text-text-secondary" id={`${id}-hint`}>
          {hint}
        </p>
      ) : null}
      <div className="mt-2">{children}</div>
      {error ? (
        <p
          className="mt-2 text-ui font-medium text-brand-strong"
          id={`${id}-error`}
        >
          <span className="sr-only">Error:</span> {error}
        </p>
      ) : null}
    </div>
  );
}
